import { useCallback, useEffect, useRef, useState } from "react";

// Web Speech API types (not in default lib.dom.d.ts)
interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  [index: number]: { transcript: string; confidence: number };
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: { length: number; [index: number]: SpeechRecognitionResult };
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface WindowWithSpeech extends Window {
  SpeechRecognition?: new () => SpeechRecognitionInstance;
  webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
}

const getSpeechRecognitionAPI = ():
  | (new () => SpeechRecognitionInstance)
  | null => {
  if (typeof window === "undefined") return null;
  const w = window as WindowWithSpeech;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

type UseSpeechRecognitionOptions = {
  onTranscript: (transcript: string) => void;
  onError?: (error: string) => void;
  onPermissionDenied?: () => void;
  lang?: string;
};

type UseSpeechRecognitionReturn = {
  isListening: boolean;
  isSupported: boolean;
  toggleListening: () => void;
};

export const useSpeechRecognition = ({
  onTranscript,
  onError,
  onPermissionDenied,
  lang = "en-US",
}: UseSpeechRecognitionOptions): UseSpeechRecognitionReturn => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  // Use ref to track listening state synchronously, avoiding race conditions
  // from stale closures when button is clicked rapidly
  const isListeningRef = useRef(false);
  const SpeechRecognitionAPI = getSpeechRecognitionAPI();

  const toggleListening = useCallback(() => {
    if (!SpeechRecognitionAPI) {
      onError?.("Speech recognition not supported in this browser");
      return;
    }

    // Check ref (not state) to prevent race condition from rapid clicks
    if (isListeningRef.current) {
      recognitionRef.current?.stop();
      isListeningRef.current = false;
      setIsListening(false);
      return;
    }

    // Mark as listening immediately to prevent concurrent starts
    isListeningRef.current = true;

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event) => {
      let finalTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const { transcript } = event.results[i][0];
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        }
      }
      if (finalTranscript) {
        onTranscript(finalTranscript);
      }
    };

    recognition.onerror = (event) => {
      // eslint-disable-next-line no-console
      console.error("Speech recognition error:", event.error);
      isListeningRef.current = false;
      setIsListening(false);
      if (event.error === "not-allowed") {
        onPermissionDenied?.();
      } else {
        onError?.(event.error);
      }
    };

    recognition.onend = () => {
      isListeningRef.current = false;
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [SpeechRecognitionAPI, lang, onTranscript, onError, onPermissionDenied]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  return {
    isListening,
    isSupported: SpeechRecognitionAPI !== null,
    toggleListening,
  };
};
